/** Підписи видів, походження й статусів понять — українською; невідоме значення не ламає інтерфейс. */

type KindMeta = { one: string; many: string; order: number };

const KINDS: Record<string, KindMeta> = {
  abbreviation: { one: 'Скорочення', many: 'Скорочення', order: 1 },
  term: { one: 'Поняття', many: 'Поняття', order: 2 },
  role: { one: 'Роль', many: 'Ролі', order: 3 },
  process: { one: 'Процес', many: 'Процеси', order: 4 },
  field: { one: 'Поле', many: 'Поля', order: 5 },
  metric: { one: 'Показник', many: 'Показники', order: 6 },
  document: { one: 'Документ', many: 'Документи', order: 7 },
  status: { one: 'Стан', many: 'Стани', order: 8 },
};

const OTHER: KindMeta = { one: 'Інше', many: 'Інше', order: 99 };

/** Ключ групи: відомий вид або 'other'. */
export function kindKey(kind: string): string {
  return Object.prototype.hasOwnProperty.call(KINDS, kind) ? kind : 'other';
}

export function kindMeta(kind: string): KindMeta {
  return KINDS[kindKey(kind)] ?? OTHER;
}

const ORIGINS: Record<string, string> = {
  official_list: 'Офіційний «Список скорочень»',
  other_documents: 'Інші документи фірми',
  internal_term: 'Внутрішній термін регламентів',
  system: 'Термін системи',
};

export function originLabel(origin: string | null): string | null {
  if (!origin) return null;
  return ORIGINS[origin] ?? null;
}

/**
 * Статуси понять за контрактом словника (confirmed | draft | owner_decision_needed | deprecated);
 * старі значення (active, ambiguous, unknown) лишаються сумісними.
 */
const STATUSES: Record<string, string> = {
  confirmed: 'Підтверджено',
  draft: 'Чернетка',
  owner_decision_needed: 'Потребує рішення власника',
  deprecated: 'Застаріле',
  active: 'Чинне',
  ambiguous: 'Неоднозначне',
  unknown: 'Без розшифровки',
};

/** Порядок статусів у фільтрі довідника. */
export const STATUS_ORDER = ['confirmed', 'draft', 'owner_decision_needed', 'deprecated'] as const;

/** Статус, з яким поняття подається як усталене (без позначки в переліку). */
export function isSettledStatus(status: string): boolean {
  return status === 'confirmed' || status === 'active';
}

export function statusLabel(status: string, serverLabel?: string | null): string {
  return serverLabel?.trim() || STATUSES[status] || 'Інше';
}

/** Пояснення для неусталених статусів (у картці й довіднику). */
export function statusNote(status: string): string | null {
  if (status === 'owner_decision_needed') return 'Визначення немає або джерела розходяться — потрібне рішення власника.';
  if (status === 'draft') return 'Чернетка: визначення ще не підтверджено джерелами.';
  if (status === 'ambiguous') return 'Скорочення неоднозначне: значення залежить від контексту.';
  if (status === 'unknown') return 'Розшифровки в документах фірми немає.';
  if (status === 'deprecated') return 'Поняття застаріле — не використовуйте його в нових документах.';
  return null;
}

// ——— Поля інтерфейсу людською мовою ———

const MODULES: Record<string, string> = {
  task: 'Задача',
  legal: 'Юридичний контур',
  content: 'Контент',
  kb: 'Процеси й знання',
  ai: 'ШІ',
  automation: 'Автоматизація',
  time: 'Облік часу',
  mgmt: 'Управлінський контур',
  people: 'Персонал',
};

const PARTS: Record<string, string> = {
  client: 'клієнт',
  matter: 'замовлення',
  oe: 'ОЕ',
  material: 'матеріал',
  passport: 'паспорт послуги',
  audience: 'аудиторія',
  process: 'процес',
  position: 'посада',
  document: 'документ',
  article: 'стаття',
  course: 'курс',
  lesson: 'урок',
  service: 'послуга',
  portrait: 'портрет клієнта',
  clientNeed: 'потреба клієнта',
  need: 'потреба',
  commercialValue: 'комерційна цінність',
  targetAudience: 'цільова аудиторія',
  valuableFinalProduct: 'ЦКП',
  level: 'рівень',
  folder: 'посадова папка',
  acknowledgement: 'ознайомлення',
  attestation: 'атестація',
  quality: 'якість',
  owner: 'власник',
  evidence: 'доказовість',
  category: 'категорія',
};

/**
 * Ключ поля → людський опис: `legal.client.portrait` → «Юридичний контур › клієнт › портрет клієнта»,
 * `task.customField:Портрет клієнта` → «Поле задачі «Портрет клієнта»». Невідомі частини лишаються як є.
 */
export function describeFieldKey(key: string): string {
  const k = key.trim();
  const custom = /^task\.customField:(.+)$/.exec(k);
  if (custom) return `Поле задачі «${custom[1]!.trim()}»`;
  const parts = k.split('.').filter(Boolean);
  if (parts.length === 1) return PARTS[parts[0]!] ? `Поле «${PARTS[parts[0]!]}»` : k;
  const [mod, ...rest] = parts;
  const head = MODULES[mod!] ?? mod!;
  return [head, ...rest.map((p) => PARTS[p] ?? p)].join(' › ');
}

/** Порівняння назв без урахування регістру (українська локаль). */
export const sameTerm = (a: string, b: string) => a.trim().toLocaleLowerCase('uk-UA') === b.trim().toLocaleLowerCase('uk-UA');
